import { Action, Reducer } from 'redux';
import { STATE } from 'app/constants';
import { withOptionsReducer } from 'app/components/options-handler';
import { CodeMappingsListState } from 'app/area/code-mappings/types-store';
import { findModifiedItem, getModifiedItem, updatePersistedItem } from 'app/area/code-mappings/selectors';
import {
  ACTION, ClearList,
  RequestList, ReceiveList, FailList,
  ModifyItem,
  RequestSaveChanges, ReceiveSaveChanges, FailSaveChanges,
  ValidationChange,
  RequestCodetableOptions,
  ReceiveCodetableOptions,
  FailCodetableOptions,
  CopyToAll,
  RequestAccessCodetables,
  ReceiveAccessCodetables,
  FailAccessCodetables,
  RequestMetadata,
  ReceiveMetadata,
  FailMetadata,
  RequestItem,
  ReceiveItem,
  FailItem
} from './action-types';
import { deepEquals } from 'app/infrastructure/js';
import { removeValidation } from 'app/area/code-mappings/common/validation';

export const INITIAL_STATE: CodeMappingsListState = {
  requestId: 0,
  fetchingList: false,
  fetchingItemId: undefined,
  updatingExternalValue: false,
  fileName: undefined,
  importSet: undefined,
  missingMappingsExists: false,
  listItems: undefined,
  codetableIds: undefined,
  modifiedItems: [],
  validation: {},
  codetableOptions: undefined,
  fetchingCodetableAccess: undefined,
  codetableAccess: undefined,
  metadata: undefined,
  fetchingMetadata: undefined
};

const reducers: { [indexer: string]: Reducer<CodeMappingsListState, Action> } = {

  [ACTION.REQUEST_LIST]: (state: CodeMappingsListState, action: RequestList): CodeMappingsListState => {

    return {
      ...state,
      requestId: action.requestId,
      fetchingList: true
    };
  },

  [ACTION.RECEIVE_LIST]: (state: CodeMappingsListState, action: ReceiveList): CodeMappingsListState => {

    if (action.requestId !== state.requestId) {
      return state;
    }

    return {
      ...state,
      fetchingList: false,
      fileName: action.fileName,
      importSet: action.importSet,
      missingMappingsExists: action.missingMappingsExists,
      codetableIds: action.codetableIds,
      listItems: [
        ...(state.listItems || []),
        ...action.list
      ]
    };
  },

  [ACTION.FAIL_LIST]: (state: CodeMappingsListState, action: FailList): CodeMappingsListState => {

    if (action.requestId !== state.requestId) {
      return state;
    }

    return {
      ...state,
      fetchingList: false
    };
  },

  [ACTION.CLEAR_LIST]: (
    state: CodeMappingsListState,
    action: ClearList
  ): CodeMappingsListState => {
    return {
      ...state,
      requestId: action.requestId,
      fetchingList: false,
      listItems: undefined,
      modifiedItems: [],
      validation: {}
    };
  },

  [ACTION.REQUEST_ITEM]: (state: CodeMappingsListState, action: RequestItem): CodeMappingsListState => {

    return {
      ...state,
      requestId: action.requestId,
      fetchingItemId: action.params?.codetableId
    };
  },

  [ACTION.RECEIVE_ITEM]: (state: CodeMappingsListState, action: ReceiveItem): CodeMappingsListState => {

    if (action.requestId !== state.requestId) {
      return state;
    }

    return {
      ...state,
      fetchingItemId: undefined,
      listItems: [
        ...(state.listItems || []).filter(item => item.codetable.value !== action.params.codetableId),
        ...action.list
      ]
    };
  },

  [ACTION.FAIL_ITEM]: (state: CodeMappingsListState, action: FailItem): CodeMappingsListState => {

    if (action.requestId !== state.requestId) {
      return state;
    }

    return {
      ...state,
      fetchingItemId: undefined
    };
  },

  [ACTION.MODIFY_ITEM]: (state: CodeMappingsListState, action: ModifyItem): CodeMappingsListState => {

    const modifiedData = action.modifiedData;
    const previousModifiedItem = findModifiedItem(state.modifiedItems, modifiedData);

    const validation = removeValidation(modifiedData, previousModifiedItem, state.validation);
    const currentValidation = (!!modifiedData?.code?.value && !!modifiedData.codetable)
      ? state.validation
      : validation;


    return {
      ...state,
      modifiedItems: [
        ...(state.modifiedItems || []).filter(item =>
          item !== previousModifiedItem
        ),
        modifiedData
      ],
      validation: currentValidation
    };
  },

  [ACTION.COPY_TO_ALL]: (state: CodeMappingsListState, action: CopyToAll): CodeMappingsListState => {

    const modifiedItems = [
      ...state.modifiedItems.filter(item => item.codetable === action.codetableId && item.id === action.id),
      ...state.listItems
        .filter(item => item.codetable?.value === action.codetableId && item.id !== action.id)
        .map(item => {
          return {
            id: item.id,
            codetable: item.codetable?.value,
            description: item.description,
            codeCategory: action.codeCategory,
            code: { value: action.code?.value, description: action.code?.description }
          };
        })];

    const otherModifiedItems = state.modifiedItems.filter(item => item.codetable !== action.codetableId);

    return {
      ...state,
      modifiedItems: [...modifiedItems, ...otherModifiedItems]
    };
  },

  [ACTION.REQUEST_SAVE_CHANGES]: (state: CodeMappingsListState, action: RequestSaveChanges): CodeMappingsListState => {

    return {
      ...state,
      updatingExternalValue: true
    };
  },

  [ACTION.RECEIVE_SAVE_CHANGES]: (state: CodeMappingsListState, action: ReceiveSaveChanges): CodeMappingsListState => {

    return {
      ...state,
      listItems: (state.listItems || []).map(persistedItem => {
        const modifiedText = getModifiedItem(state.modifiedItems || [], persistedItem);

        return updatePersistedItem(persistedItem, modifiedText);
      }),
      modifiedItems: [],
      updatingExternalValue: false
    };
  },

  [ACTION.FAIL_SAVE_CHANGES]: (state: CodeMappingsListState, action: FailSaveChanges): CodeMappingsListState => {

    return {
      ...state,
      modifiedItems: [],
      updatingExternalValue: false
    };
  },

  [ACTION.REQUEST_CODETABLE_OPTIONS]: (state: CodeMappingsListState, action: RequestCodetableOptions): CodeMappingsListState => {
    return {
      ...state,
      codetableOptions: {
        ...(state.codetableOptions || {}),
        [action.id]: {
          fetching: true,
          loadOptionsParams: action.loadOptionsParams,
          codes: undefined,
          currentOptions: undefined
        }
      }
    };
  },

  [ACTION.RECEIVE_CODETABLE_OPTIONS]: (state: CodeMappingsListState, action: ReceiveCodetableOptions): CodeMappingsListState => {

    if (!state.codetableOptions || !state.codetableOptions[action.id] ||
      !deepEquals(state.codetableOptions[action.id].loadOptionsParams, action.loadOptionsParams)) {
      return state;
    }

    return {
      ...state,
      codetableOptions: {
        ...state.codetableOptions,
        [action.id]: {
          ...state.codetableOptions[action.id],
          fetching: false,
          codes: action.codes,
          currentOptions: action.currentOptions
        }
      }
    };
  },

  [ACTION.FAIL_CODETABLE_OPTIONS]: (state: CodeMappingsListState, action: FailCodetableOptions): CodeMappingsListState => {

    if (!state.codetableOptions || !state.codetableOptions[action.id] ||
      !deepEquals(state.codetableOptions[action.id].loadOptionsParams, action.loadOptionsParams)) {
      return state;
    }

    return {
      ...state,
      codetableOptions: {
        ...state.codetableOptions,
        [action.id]: {
          ...state.codetableOptions[action.id],
          fetching: false
        }
      }
    };
  },

  [ACTION.VALIDATION_CHANGE]: (state: CodeMappingsListState, action: ValidationChange): CodeMappingsListState => {

    return {
      ...state,
      validation: action.validation
    };
  },

  [ACTION.REQUEST_ACCESS_CODETABLES]: (state: CodeMappingsListState, action: RequestAccessCodetables): CodeMappingsListState => {

    return {
      ...state,
      fetchingCodetableAccess: action.id
    };
  },

  [ACTION.RECEIVE_ACCESS_CODETABLES]: (state: CodeMappingsListState, action: ReceiveAccessCodetables): CodeMappingsListState => {

    return action.id === state.fetchingCodetableAccess
      ? {
        ...state,
        fetchingCodetableAccess: undefined,
        codetableAccess: action.accessCodetables
      }
      : state;
  },

  [ACTION.FAIL_ACCESS_CODETABLES]: (state: CodeMappingsListState, action: FailAccessCodetables): CodeMappingsListState => {

    return action.id === state.fetchingCodetableAccess
      ? {
        ...state,
        fetchingCodetableAccess: undefined
      }
      : state;
  },

  [ACTION.REQUEST_METADATA]: (state: CodeMappingsListState, action: RequestMetadata): CodeMappingsListState => {

    return {
      ...state,
      fetchingMetadata: action.id
    };
  },

  [ACTION.RECEIVE_METADATA]: (state: CodeMappingsListState, action: ReceiveMetadata): CodeMappingsListState => {

    return action.id === state.fetchingMetadata
      ? {
        ...state,
        fetchingMetadata: undefined,
        metadata: action.metadata
      }
      : state;
  },

  [ACTION.FAIL_METADATA]: (state: CodeMappingsListState, action: FailMetadata): CodeMappingsListState => {

    return action.id === state.fetchingMetadata
      ? {
        ...state,
        fetchingMetadata: undefined
      }
      : state;
  }
};

const stateReducer: Reducer<CodeMappingsListState> =
  (state: CodeMappingsListState = INITIAL_STATE, action: any) => {
    const r = reducers[action.type];
    return (r && r(state, action)) || state;
  };

export const reducer = withOptionsReducer(STATE.codeMappings, stateReducer);
